import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { TrendChart, Sparkline } from "../components/charts";
import {
  useTakebackStats,
  useDailyTakebackCounts,
  useTakebacks,
} from "../lib/tes-hooks";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";

interface Program {
  id: string;
  name: string;
  type: "takeback" | "trade_in" | "resale";
  status: string;
  config: string | Record<string, unknown>;
  external_id: string | null;
  created_at: string;
}

export const Route = createFileRoute("/programs_/$id_/dashboard")({
  component: ProgramDashboard,
});

// --- Mock data for demo ---
function generateMockDailyCounts(): Array<{ date: string; count: number }> {
  const data: Array<{ date: string; count: number }> = [];
  const now = new Date();
  const baseCounts = [35, 42, 38, 45, 41, 28, 42];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const noise = 0.85 + Math.random() * 0.3;
    data.push({
      date: date.toISOString().split("T")[0],
      count: Math.round(baseCounts[6 - i] * noise),
    });
  }
  return data;
}

const MOCK_TAKEBACK_ACTIVITY = [
  {
    id: "tb-mc-001",
    type: "Ultraboost 22",
    status: "completed",
    value: 45.0,
    time: "2 hours ago",
  },
  {
    id: "tb-mc-002",
    type: "Stan Smith",
    status: "pending",
    value: 28.0,
    time: "3 hours ago",
  },
  {
    id: "tb-mc-003",
    type: "NMD_R1",
    status: "shipped",
    value: 35.0,
    time: "5 hours ago",
  },
  {
    id: "tb-mc-004",
    type: "Forum Low",
    status: "completed",
    value: 42.0,
    time: "6 hours ago",
  },
  {
    id: "tb-mc-005",
    type: "Samba OG",
    status: "graded",
    value: 38.0,
    time: "8 hours ago",
  },
];

const MOCK_RESALE_TRANSACTIONS = [
  {
    id: "rs-mc-001",
    item: "Ultraboost 22",
    seller: "James K.",
    buyer: "Sarah M.",
    price: 52.0,
    status: "completed",
    time: "1 hour ago",
  },
  {
    id: "rs-mc-002",
    item: "Forum Low",
    seller: "Maria L.",
    buyer: "Tom W.",
    price: 68.0,
    status: "completed",
    time: "3 hours ago",
  },
  {
    id: "rs-mc-003",
    item: "Samba OG",
    seller: "Chen R.",
    buyer: "Alex P.",
    price: 45.0,
    status: "shipped",
    time: "5 hours ago",
  },
  {
    id: "rs-mc-004",
    item: "Stan Smith",
    seller: "Emily D.",
    buyer: "Jordan B.",
    price: 38.0,
    status: "pending",
    time: "7 hours ago",
  },
  {
    id: "rs-mc-005",
    item: "Gazelle",
    seller: "David H.",
    buyer: "Lisa N.",
    price: 55.0,
    status: "completed",
    time: "9 hours ago",
  },
];

function generateResaleDailyCounts(): Array<{ date: string; count: number }> {
  const data: Array<{ date: string; count: number }> = [];
  const now = new Date();
  const baseCounts = [1250, 1480, 1320, 1890, 1650, 980, 1740];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const noise = 0.9 + Math.random() * 0.2;
    data.push({
      date: date.toISOString().split("T")[0],
      count: Math.round(baseCounts[6 - i] * noise),
    });
  }
  return data;
}

function DemoBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
      style={{ backgroundColor: "rgba(0,251,169,0.15)", color: "#171717" }}
    >
      Demo Data
    </span>
  );
}

function ProgramDashboard() {
  const params = Route.useParams() as { id: string };
  const id = params.id;
  const { tenantId } = useTenant();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Reload when program ID or tenant changes
  useEffect(() => {
    setLoading(true);
    setError("");
    fetchProgram();
  }, [id, tenantId]);

  const fetchProgram = async () => {
    try {
      const response = await tenantFetch(`/api/programs/${id}`);
      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          setProgram(data);
          return;
        }
      }
      // Fallback to demo data
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError("Program not found");
      }
    } catch {
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError("Failed to load program");
      }
    } finally {
      setLoading(false);
    }
  };

  function getDemoProgram(tid: string, pid: string): Program | null {
    if (tid === "arcteryx") {
      const programs: Record<string, Program> = {
        "prg-arc-1": {
          id: "prg-arc-1",
          name: "Arc'teryx ReBIRD",
          type: "trade_in",
          status: "active",
          config: { tes_client_id: "arcteryx-rebird-prod" },
          external_id: "arcteryx-rebird-prod",
          created_at: "2024-01-15T10:00:00Z",
        },
        "prg-arc-2": {
          id: "prg-arc-2",
          name: "Arc'teryx Repair Program",
          type: "takeback",
          status: "draft",
          config: {},
          external_id: null,
          created_at: "2024-06-01T10:00:00Z",
        },
      };
      return programs[pid] || null;
    }
    const defaultPrograms: Record<string, Program> = {
      "prg-lulu-1": {
        id: "prg-lulu-1",
        name: "Like New Trade-In",
        type: "trade_in",
        status: "active",
        config: { tes_client_id: "lululemon-likenew-prod" },
        external_id: "lululemon-likenew-prod",
        created_at: "2024-01-01T10:00:00Z",
      },
    };
    return defaultPrograms[pid] || null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="text-center py-12">
        <h1 className="text-xl font-light mb-2" style={{ color: "#171717" }}>
          {error || "Program not found"}
        </h1>
        <Link to="/programs" className="text-sm" style={{ color: "#00FBA9" }}>
          Back to Programs
        </Link>
      </div>
    );
  }

  const programWithParsedConfig = {
    ...program,
    config:
      typeof program.config === "string"
        ? JSON.parse(program.config)
        : program.config,
  };

  return (
    <div>
      {/* Header with Program Info */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 flex items-center justify-center"
            style={{ backgroundColor: "#00FBA9" }}
          >
            <ProgramIcon type={program.type} />
          </div>
          <div>
            <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
              {program.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span
                className="px-2 py-0.5 rounded-full text-xs font-light capitalize"
                style={{
                  backgroundColor:
                    program.status === "active" ? "#00FBA9" : "#FAFAFA",
                  color: program.status === "active" ? "#171717" : "#A9A9A9",
                }}
              >
                {program.status}
              </span>
              <span className="text-sm capitalize" style={{ color: "#A9A9A9" }}>
                {program.type.replace(/_/g, " ")} Program
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Link
            to="/programs/$id/analytics"
            params={{ id: program.id }}
            className="px-4 py-2  text-sm font-light flex items-center gap-2"
            style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
          >
            <ChartIcon />
            Analytics
          </Link>
          <a
            href={`/programs/${program.id}/resale`}
            className="px-4 py-2  text-sm font-light flex items-center gap-2"
            style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
          >
            <ShopIcon size={16} />
            Resale
          </a>
          <Link
            to="/programs/$id/takebacks"
            params={{ id: program.id }}
            className="px-4 py-2  text-sm font-light flex items-center gap-2"
            style={{ backgroundColor: "#171717", color: "#FFFFFF" }}
          >
            <ListIcon />
            View All
          </Link>
        </div>
      </div>

      {/* Dashboard Content */}
      {program.type === "takeback" || program.type === "trade_in" ? (
        <TakebackDashboard program={programWithParsedConfig} />
      ) : (
        <ResaleDashboard program={programWithParsedConfig} />
      )}
    </div>
  );
}

// Takeback Dashboard - Quick Overview
function TakebackDashboard({ program }: { program: Program }) {
  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );
  const useDemo = !isConfigured;

  const { data: stats, isLoading: statsLoading } = useTakebackStats(
    program,
    "7d",
  );
  const { data: dailyCounts } = useDailyTakebackCounts(program, 7);
  const { data: recentTakebacks, isLoading: takebacksLoading } = useTakebacks(
    program,
    { limit: 5 },
  );

  // Mock data for demo
  const mockDaily = useMemo(
    () => (useDemo ? generateMockDailyCounts() : null),
    [useDemo],
  );
  const mockStats = useMemo(
    () =>
      useDemo
        ? { total: 287, byStatus: { pending: 18, completed: 269 } }
        : null,
    [useDemo],
  );

  const effectiveDaily = useDemo ? mockDaily : dailyCounts;
  const effectiveStats = useDemo ? mockStats : stats;
  const isLoading = useDemo ? false : statsLoading;
  const isTakebacksLoading = useDemo ? false : takebacksLoading;

  // Calculate quick stats
  const todayCount = effectiveDaily?.[effectiveDaily.length - 1]?.count || 0;
  const yesterdayCount =
    effectiveDaily?.[effectiveDaily.length - 2]?.count || 0;
  const todayTrend =
    yesterdayCount > 0
      ? ((todayCount - yesterdayCount) / yesterdayCount) * 100
      : 0;

  // Transaction fees: sum of 3% of each takeback value
  const totalTakebackValue = useDemo
    ? MOCK_TAKEBACK_ACTIVITY.reduce((sum, t) => sum + t.value, 0) * (287 / 5) // extrapolate from 5 samples
    : 0;
  const transactionFees = totalTakebackValue * 0.03;

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}

      {/* Today's Snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <TodayCard
          label="Today"
          value={isLoading ? "-" : todayCount}
          trend={todayTrend}
          sparklineData={effectiveDaily?.map((d) => d.count) || []}
        />
        <QuickStatCard
          label="This Week"
          value={isLoading ? "-" : effectiveStats?.total || 0}
          sublabel="takebacks"
        />
        <QuickStatCard
          label="Pending"
          value={isLoading ? "-" : effectiveStats?.byStatus?.pending || 0}
          sublabel="awaiting action"
          highlight
        />
        <QuickStatCard
          label="Completed"
          value={isLoading ? "-" : effectiveStats?.byStatus?.completed || 0}
          sublabel="processed"
        />
        <FeeCard
          label="Transaction Fees"
          value={
            useDemo ? `$${Math.round(transactionFees).toLocaleString()}` : "$0"
          }
          sublabel="3% per transaction"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-light" style={{ color: "#171717" }}>
              Recent Activity
            </h2>
            <Link
              to="/programs/$id/takebacks"
              params={{ id: program.id }}
              className="text-xs"
              style={{ color: "#00FBA9" }}
            >
              View all →
            </Link>
          </div>

          {isTakebacksLoading ? (
            <div className="h-48 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : useDemo ? (
            <div className="space-y-3">
              {MOCK_TAKEBACK_ACTIVITY.map((tb) => (
                <ActivityRow
                  key={tb.id}
                  id={tb.id}
                  type={tb.type}
                  status={tb.status}
                  time={tb.time}
                  fee={`$${(tb.value * 0.03).toFixed(2)}`}
                />
              ))}
            </div>
          ) : (recentTakebacks?.items?.length || 0) > 0 ? (
            <div className="space-y-3">
              {recentTakebacks?.items.slice(0, 5).map((tb) => {
                const latestStatus = tb.status_history?.length
                  ? tb.status_history[tb.status_history.length - 1]?.status
                  : "unknown";
                return (
                  <ActivityRow
                    key={tb.id}
                    id={tb.id}
                    type={tb.type_name}
                    status={latestStatus}
                    time={new Date(tb.created_at * 1000).toLocaleString()}
                  />
                );
              })}
            </div>
          ) : (
            <div
              className="h-48 flex items-center justify-center"
              style={{ color: "#A9A9A9" }}
            >
              No recent takebacks
            </div>
          )}
        </div>

        {/* Weekly Trend */}
        <div className="card">
          <h2 className="text-sm font-light mb-4" style={{ color: "#171717" }}>
            7-Day Trend
          </h2>
          <div className="h-32">
            {effectiveDaily && effectiveDaily.length > 0 ? (
              <TrendChart data={effectiveDaily} height={120} />
            ) : (
              <div
                className="h-full flex items-center justify-center"
                style={{ color: "#A9A9A9" }}
              >
                No data yet
              </div>
            )}
          </div>
          <div
            className="mt-4 pt-4"
            style={{ borderTop: "1px solid rgba(169,169,169,0.1)" }}
          >
            <div className="flex justify-between text-sm">
              <span style={{ color: "#A9A9A9" }}>7-day total</span>
              <span style={{ color: "#171717" }}>
                {(effectiveStats?.total || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Footer */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCard
          icon={<ChartIcon />}
          label="View Analytics"
          description="Deep dive into metrics"
          to={`/programs/${program.id}/analytics`}
        />
        <ActionCard
          icon={<ListIcon />}
          label="Browse Takebacks"
          description="View and search all takebacks"
          to={`/programs/${program.id}/takebacks`}
        />
        <ActionCard
          icon={<ShopIcon size={16} />}
          label="Resale Transactions"
          description="View peer-to-peer sales"
          to={`/programs/${program.id}/resale`}
        />
      </div>
    </div>
  );
}

// Resale Dashboard - Full mock implementation
function ResaleDashboard({ program }: { program: Program }) {
  const mockDaily = useMemo(() => generateResaleDailyCounts(), []);

  const gmv = 12450;
  const listings = 89;
  const sales = 34;
  const txFees = Math.round(gmv * 0.03);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <DemoBadge />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card" style={{ backgroundColor: "#171717" }}>
          <p className="text-xs mb-2" style={{ color: "#A9A9A9" }}>
            GMV
          </p>
          <p className="text-3xl font-light" style={{ color: "#FFFFFF" }}>
            ${gmv.toLocaleString()}
          </p>
          <p className="text-xs mt-1" style={{ color: "#00FBA9" }}>
            ↑ 18% vs last week
          </p>
        </div>
        <QuickStatCard
          label="Active Listings"
          value={listings}
          sublabel="items for sale"
        />
        <QuickStatCard
          label="Sales"
          value={sales}
          sublabel="completed this week"
        />
        <FeeCard
          label="Transaction Fees"
          value={`$${txFees}`}
          sublabel="3% per sale"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Resale Transactions */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-light" style={{ color: "#171717" }}>
              Recent Resale Transactions
            </h2>
            <a
              href={`/programs/${program.id}/resale`}
              className="text-xs"
              style={{ color: "#00FBA9" }}
            >
              View all →
            </a>
          </div>
          <div className="space-y-3">
            {MOCK_RESALE_TRANSACTIONS.map((tx) => (
              <a
                key={tx.id}
                href={`/pin/resale/${tx.id}`}
                className="flex items-center justify-between py-2 px-3 hover:shadow-sm transition-shadow"
                style={{ backgroundColor: "#FAFAFA", display: "flex" }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: getStatusColor(tx.status) }}
                  />
                  <div>
                    <p className="text-sm" style={{ color: "#171717" }}>
                      {tx.item}
                    </p>
                    <p className="text-xs" style={{ color: "#A9A9A9" }}>
                      {tx.seller} → {tx.buyer}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    ${tx.price.toFixed(2)}
                  </p>
                  <p className="text-xs" style={{ color: "#00FBA9" }}>
                    Fee: ${(tx.price * 0.03).toFixed(2)}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Sales Trend */}
        <div className="card">
          <h2 className="text-sm font-light mb-4" style={{ color: "#171717" }}>
            7-Day Sales Trend
          </h2>
          <div className="h-32">
            <TrendChart data={mockDaily} height={120} />
          </div>
          <div
            className="mt-4 pt-4"
            style={{ borderTop: "1px solid rgba(169,169,169,0.1)" }}
          >
            <div className="flex justify-between text-sm">
              <span style={{ color: "#A9A9A9" }}>7-day GMV</span>
              <span style={{ color: "#171717" }}>${gmv.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span style={{ color: "#A9A9A9" }}>7-day fees</span>
              <span style={{ color: "#00FBA9" }}>${txFees}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Footer */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCard
          icon={<ChartIcon />}
          label="View Analytics"
          description="Deep dive into resale metrics"
          to={`/programs/${program.id}/analytics`}
        />
        <ActionCard
          icon={<ShopIcon size={16} />}
          label="All Resale Transactions"
          description="Browse and search resale activity"
          to={`/programs/${program.id}/resale`}
        />
        <ActionCard
          icon={<SettingsIcon />}
          label="Program Settings"
          description="Configure program options"
          to={`/programs/${program.id}`}
        />
      </div>
    </div>
  );
}

// Helper Components
function TodayCard({
  label,
  value,
  trend,
  sparklineData,
}: {
  label: string;
  value: string | number;
  trend: number;
  sparklineData: number[];
}) {
  return (
    <div className="card" style={{ backgroundColor: "#171717" }}>
      <p className="text-xs mb-2" style={{ color: "#A9A9A9" }}>
        {label}
      </p>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-3xl font-light" style={{ color: "#FFFFFF" }}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          <p
            className="text-xs mt-1 flex items-center gap-1"
            style={{
              color: trend > 0 ? "#00FBA9" : trend < 0 ? "#FF4C00" : "#A9A9A9",
            }}
          >
            {trend > 0 ? "↑" : trend < 0 ? "↓" : "→"}
            {Math.abs(trend).toFixed(0)}% vs yesterday
          </p>
        </div>
        <Sparkline data={sparklineData} color="#00FBA9" />
      </div>
    </div>
  );
}

function QuickStatCard({
  label,
  value,
  sublabel,
  highlight,
}: {
  label: string;
  value: string | number;
  sublabel: string;
  highlight?: boolean;
}) {
  return (
    <div className="card">
      <p className="text-xs mb-2" style={{ color: "#A9A9A9" }}>
        {label}
      </p>
      <p
        className="text-2xl font-light"
        style={{ color: highlight ? "#F5A623" : "#171717" }}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
        {sublabel}
      </p>
    </div>
  );
}

function FeeCard({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel: string;
}) {
  return (
    <div className="card" style={{ border: "1px solid rgba(0,251,169,0.3)" }}>
      <p className="text-xs mb-2" style={{ color: "#A9A9A9" }}>
        {label}
      </p>
      <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
        {value}
      </p>
      <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
        {sublabel}
      </p>
    </div>
  );
}

function ActivityRow({
  id,
  type,
  status,
  time,
  fee,
}: {
  id: string;
  type: string;
  status: string;
  time: string;
  fee?: string;
}) {
  return (
    <div
      className="flex items-center justify-between py-2 px-3 "
      style={{ backgroundColor: "#FAFAFA" }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: getStatusColor(status) }}
        />
        <div>
          <p className="text-sm font-mono" style={{ color: "#171717" }}>
            {id.length > 12 ? `${id.slice(0, 12)}...` : id}
          </p>
          <p className="text-xs" style={{ color: "#A9A9A9" }}>
            {type}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs capitalize" style={{ color: "#171717" }}>
          {status.replace(/_/g, " ")}
        </p>
        {fee && (
          <p className="text-xs" style={{ color: "#00FBA9" }}>
            Fee: {fee}
          </p>
        )}
        <p className="text-xs" style={{ color: "#A9A9A9" }}>
          {time}
        </p>
      </div>
    </div>
  );
}

function ActionCard({
  icon,
  label,
  description,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="card flex items-center gap-4 hover:shadow-md transition-shadow"
    >
      <div
        className="w-10 h-10  flex items-center justify-center"
        style={{ backgroundColor: "#FAFAFA" }}
      >
        {icon}
      </div>
      <div>
        <p className="text-sm font-light" style={{ color: "#171717" }}>
          {label}
        </p>
        <p className="text-xs" style={{ color: "#A9A9A9" }}>
          {description}
        </p>
      </div>
    </Link>
  );
}

function LoadingSpinner() {
  return (
    <div
      className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
      style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }}
    />
  );
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    completed: "#00FBA9",
    processed: "#00FBA9",
    shipped: "#4A90D9",
    in_transit: "#4A90D9",
    delivered: "#7B61FF",
    graded: "#F5A623",
    pending: "#F5A623",
    created: "#A9A9A9",
  };
  return colors[status.toLowerCase()] || "#A9A9A9";
}

// Icons
function ProgramIcon({ type }: { type: string }) {
  if (type === "resale") {
    return <ShopIcon color="#171717" />;
  }
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#171717"
      strokeWidth="1.5"
    >
      <path
        d="M4 4V9H4.58152M19.9381 11C19.446 7.05369 16.0796 4 12 4C8.64262 4 5.76829 6.06817 4.58152 9M4.58152 9H9M20 20V15H19.4185M19.4185 15C18.2317 17.9318 15.3574 20 12 20C7.92038 20 4.55399 16.9463 4.06189 13M19.4185 15H15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9 19V13M12 19V9M15 19V15M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ShopIcon({
  color = "currentColor",
  size = 28,
}: {
  color?: string;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    >
      <path
        d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 22V12H15V22" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
